// Lead attribution and URL handling for the audit form.

const SOURCE_KEY = 'hh_source'
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

/**
 * Returns where the visitor came from as a short string: the utm params of the landing URL
 * (`utm_source=google&utm_medium=cpc`), else `referrer=<host>` for an external referrer.
 * The first value seen in the tab is kept in sessionStorage, so it survives in-app navigation.
 */
export function readSource(search: string, referrer: string, ownHost: string): string | undefined {
  const params = new URLSearchParams(search)
  const utm = UTM_KEYS.flatMap((k) => {
    const v = params.get(k)?.trim()
    return v ? [`${k}=${v}`] : []
  })
  if (utm.length) return utm.join('&').slice(0, 200)
  if (!referrer) return undefined
  try {
    const host = new URL(referrer).hostname
    return host && host !== ownHost ? `referrer=${host}`.slice(0, 200) : undefined
  } catch {
    return undefined
  }
}

export function captureSource(): string | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const kept = sessionStorage.getItem(SOURCE_KEY)
    if (kept) return kept
  } catch {
    // storage blocked: fall through and compute without persisting
  }
  const source = readSource(location.search, document.referrer, location.hostname)
  if (source) {
    try {
      sessionStorage.setItem(SOURCE_KEY, source)
    } catch {
      // ignore
    }
  }
  return source
}

/**
 * Accepts what people type ("mysite.com", "www.mysite.com/about", "http://mysite.com")
 * and returns an absolute http(s) URL, or null when it can't be a public web address.
 */
export function normalizeSiteUrl(raw: string): string | null {
  const text = raw.trim()
  if (!text || /\s/.test(text)) return null
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`
  let url: URL
  try {
    url = new URL(withScheme)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  const host = url.hostname
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) return null
  return url.toString()
}
